import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import Card, { useContrastedBackground } from '../../../components/Card'
import CharacterAvatar from '../../../components/CharacterAvatar'
import CharacterClassIcon from '../../../components/CharacterClassIcon'
import ItemLevelBadge from '../../TeamDashboard/subpages/TeamRoster/components/ItemLevelBadge'
import { useCharacterProgress } from '../../../config/queries'
import { getRaidProgressSummary } from '../../../config/column-definitions/character'
import { useDifficulties } from '../../../hooks/useDifficulties'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'

interface Props {
  character: wowaudit.Character
}

const AltStat = ({
  label,
  value,
  className,
  style,
}: {
  label: string
  value: ReactNode
  className?: string
  style?: React.CSSProperties
}) => {
  const background = useContrastedBackground()

  return (
    <div
      className={cn(
        background,
        'border rounded-lg flex items-center gap-2 px-2.5 py-1',
        className,
      )}
    >
      <span className='text-xs text-muted-foreground font-semibold'>
        {label}
      </span>
      <span className='text-sm leading-tight font-medium' style={style}>
        {value}
      </span>
    </div>
  )
}

const Alts = ({ character }: Props) => {
  const { data: progress } = useCharacterProgress(character.id)
  const gameVersion = useGameVersion()
  const { defaultDifficulty } = useDifficulties()
  const alts = progress?.alts ?? []

  if (alts.length === 0) return null

  return (
    <div className='mt-6'>
      <h3 className='mb-2'>Alts</h3>
      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3'>
        {alts.map((alt) => {
          const details = progress?.altDetails[alt.id]

          return (
            <Link key={alt.id} to={alt.path} className='group block'>
              <Card className='flex w-full items-center gap-3 py-2 px-3 transition-colors group-hover:border-primary/40'>
                <CharacterAvatar
                  character={alt}
                  className='h-14 w-14 shrink-0 rounded-md border'
                />
                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                  <span className='flex items-center gap-2 text-base font-medium'>
                    <CharacterClassIcon characterClass={alt.characterClass} />
                    <span
                      className={cn(classColours[alt.characterClass]?.text)}
                    >
                      {alt.name}
                    </span>
                    <ItemLevelBadge
                      size='lg'
                      details={details}
                      level={alt.level}
                      className='ml-auto'
                    />
                  </span>
                  {details?.maxItemLevel ?? details?.mythicRating ? (
                    <span className='flex items-center gap-2'>
                      {gameVersion.has('mythic_plus') ? (
                        <AltStat
                          label='M+'
                          value={details.mythicRating ?? '-'}
                          style={{ color: details.mythicRatingColor?.overall }}
                        />
                      ) : null}
                      <AltStat
                        label='Raids'
                        value={
                          details.currentRaidProgression
                            ? getRaidProgressSummary(
                                details.currentRaidProgression,
                                defaultDifficulty,
                              ).element
                            : '-'
                        }
                      />
                    </span>
                  ) : (
                    <span className='text-sm italic text-muted-foreground'>
                      No data available
                    </span>
                  )}
                </div>
              </Card>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

export default Alts
